import React, { useEffect, useMemo, useState } from 'react';
import { DatePicker, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { adminApi } from '@services/api';
import { CountryFilter } from './CountryFilter';
import { MonetizationBoard } from './MonetizationBoard';
import {
  MAX_RANGE_DAYS,
  addDays,
  defaultRange,
  istDay,
  pickedTooLong,
  windowForPicked,
} from './monetizationMath';
import { COUNTRY_TABS } from './monetizationTabState';
import { useMonetizationTab } from './useMonetizationTab';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const DAY = 'YYYY-MM-DD';

/**
 * Monetization: KPIs against the 15 Dec 2026 targets, SKUs, banner and offer funnels, ads, the
 * soft-currency economy, spender tiers and experiments, from the BigQuery rollups (never the DB
 * replica). Dates are IST days. Each tab is fetched when first opened; the data is daily and
 * cached 15 minutes by the backend, so there is no polling.
 */
export const MonetizationPage: React.FC = () => {
  const initial = useMemo(() => defaultRange(Date.now()), []);
  const { state, activeTab, openTab, setFilters, retry } = useMonetizationTab(initial);
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [experimentKeys, setExperimentKeys] = useState<string[]>([]);
  const [keysRequested, setKeysRequested] = useState(false);

  // The selector lists the experiments the admin already knows; fetched once, when the tab opens.
  useEffect(() => {
    if (activeTab !== 'experiments' || keysRequested) return;
    setKeysRequested(true);
    adminApi
      .getExperiments()
      .then((res) => {
        const data: unknown = res.data;
        if (!Array.isArray(data)) return;
        const keys = data
          .map((e) => (e as { key?: unknown }).key)
          .filter((k): k is string => typeof k === 'string');
        setExperimentKeys(keys.sort());
      })
      .catch((err: unknown) => console.error('Failed to load experiment keys', err));
  }, [activeTab, keysRequested]);

  const { filters } = state;
  const today = istDay(Date.now());

  const disabledDate = (current: Dayjs, info: { from?: Dayjs }) => {
    if (current.format(DAY) > today) return true;
    return info.from ? Math.abs(current.diff(info.from, 'day')) > MAX_RANGE_DAYS - 1 : false;
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>
          Monetization
        </Title>
        <Text type="secondary" style={{ fontSize: 13 }}>
          BigQuery rollups, IST days, cached for 15 minutes
        </Text>
      </div>
      <Space wrap style={{ marginBottom: 16 }} align="end">
        <RangePicker
          aria-label="Date range (IST)"
          allowClear={false}
          value={[dayjs(filters.from), dayjs(addDays(filters.to, -1))]}
          disabledDate={disabledDate}
          onChange={(range) => {
            if (!range || !range[0] || !range[1]) return;
            const first = range[0].format(DAY);
            const last = range[1].format(DAY);
            if (pickedTooLong(first, last)) {
              setRangeError(`The range is at most ${MAX_RANGE_DAYS} days.`);
              return;
            }
            setRangeError(null);
            setFilters({ ...filters, ...windowForPicked(first, last) });
          }}
        />
        {COUNTRY_TABS.includes(activeTab) && (
          <CountryFilter
            tab={activeTab}
            value={filters.country}
            onSearch={(country) => setFilters({ ...filters, country })}
          />
        )}
        {rangeError && <Text type="danger">{rangeError}</Text>}
      </Space>
      <MonetizationBoard
        state={state}
        activeTab={activeTab}
        onTabChange={openTab}
        onRetry={retry}
        experimentKeys={experimentKeys}
        onExperimentChange={(key) => setFilters({ ...filters, experimentKey: key })}
      />
    </div>
  );
};
